import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Loader2, Check, X, ShieldCheck, ArrowRight, Sparkles, Pencil,
} from "lucide-react";

const CAT = {
  grammar: { label: "Gramatica", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  facts: { label: "Fapt / cifra schimbata", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  meaning: { label: "Sens deviat", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  tone: { label: "Ton alterat", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  typography: { label: "Tipografie", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  style: { label: "Stil / titlu / structura", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  languagetool: { label: "LanguageTool", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  glossary_inconsistency: { label: "Termen din glosar", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  structure: { label: "Structura strofei", cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

const CAT_OTHER = { label: "Altele", cls: "bg-slate-50 text-slate-600 border-slate-200" };

export default function EditionReview() {
  const { id, editionId } = useParams();
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/editions/${editionId}/flags${category ? `?category=${category}` : ""}`);
      setFlags(data.flags);
    } catch (e) { toast.error("Nu s-au putut incarca semnalarile"); }
    finally { setLoading(false); }
  }, [editionId]);

  useEffect(() => { load(); }, [load]);

  const act = async (flagId, action) => {
    try {
      await api.post(`/flags/${flagId}/action`, { action });
      setFlags((f) => f.map((x) => x.id === flagId ? { ...x, status: action === "accept" ? "accepted" : "dismissed" } : x));
      toast.success(action === "accept" ? "Corectie aplicata in EPUB" : "Semnalare respinsa");
    } catch (e) { toast.error("Actiunea a esuat"); }
  };

  const startEdit = (f) => { setEditingId(f.id); setDraftText(f.translation); };
  const cancelEdit = () => { setEditingId(null); setDraftText(""); };
  const saveEdit = async (flagId) => {
    if (!draftText.trim()) { toast.error("Textul nu poate fi gol"); return; }
    setSavingEdit(true);
    try {
      await api.post(`/flags/${flagId}/manual-edit`, { text: draftText });
      setFlags((f) => f.map((x) => x.id === flagId
        ? { ...x, status: "accepted", translation: draftText, proposed_correction: draftText, manually_edited: true }
        : x));
      setEditingId(null);
      setDraftText("");
      toast.success("Editare manuala salvata in EPUB");
    } catch (e) { toast.error("Salvarea a esuat"); }
    finally { setSavingEdit(false); }
  };

  const open = flags.filter((f) => f.status === "open");
  const langName = flags[0]?.lang_name || "";

  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate(`/project/${id}`)} data-testid="back-to-project-btn"
          className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0f172a]">
          <ArrowLeft className="h-4 w-4" /> Inapoi la catalog
        </button>

        <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#d97706] font-mono2">Corector de sens · {langName}</div>
            <h1 className="font-display mt-2 text-3xl tracking-tight">Pasaje semnalate</h1>
          </div>
          <div className="rounded-full border border-[#e6e4dd] bg-white px-4 py-2 text-sm">
            <span className="font-semibold text-[#e11d48]">{open.length}</span> deschise · {flags.length} total
          </div>
        </div>

        {!loading && flags.length > 0 && (
          <div className="mt-4 flex gap-2 text-xs">
            {[["", "Toate"], ["grammar", "Gramatica"], ["meaning", "Sens"], ["facts", "Fapte"], ["tone", "Ton"], ["typography", "Tipografie"], ["style", "Stil"], ["languagetool", "LanguageTool"]].map(([val, label]) => (
              <button key={val || "all"} data-testid={`cat-filter-${val || "all"}`}
                onClick={() => { setCategory(val); }}
                className={`rounded-full border px-3 py-1 font-medium ${category === val ? "border-slate-900 bg-slate-900 text-white" : "border-[#e6e4dd] bg-white text-[#475569] hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0f172a]" /></div>
        ) : flags.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-emerald-200 bg-emerald-50 py-20 text-center">
            <ShieldCheck className="mx-auto h-10 w-10 text-[#059669]" />
            <h3 className="font-display mt-4 text-2xl">Fara probleme de sens</h3>
            <p className="mt-2 text-sm text-emerald-800">Nu s-au gasit discrepante pentru aceasta editie.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <AnimatePresence>
              {flags.map((f) => (
                <motion.div key={f.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }} data-testid={`flag-${f.id}`}
                  className={`rounded-2xl border bg-white p-6 ${f.status !== "open" ? "opacity-60" : ""}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${(CAT[f.category] || CAT_OTHER).cls}`}>
                        {(CAT[f.category] || CAT_OTHER).label}
                      </span>
                      <span className="text-xs text-[#94a3b8]">{f.chapter_title}</span>
                    </div>
                    {f.status !== "open" && (
                      <span className={`text-xs font-medium ${f.status === "accepted" ? "text-[#059669]" : "text-[#94a3b8]"}`}>
                        {f.status === "accepted" ? "Aplicata" : "Respinsa"}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-xl border border-[#e6e4dd] bg-[#f6f5f1] p-4">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#94a3b8] font-mono2">Original</div>
                      <p className="mt-2 font-display text-sm leading-relaxed text-[#0f172a]">{f.original}</p>
                    </div>
                    <div className="rounded-r-xl border-l-4 border-rose-400 bg-rose-50/60 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-rose-500 font-mono2">Traducere suspecta</div>
                        {f.status === "open" && editingId !== f.id && (
                          <button data-testid={`edit-${f.id}`} onClick={() => startEdit(f)}
                            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-rose-500 hover:text-rose-700">
                            <Pencil className="h-3 w-3" /> Editeaza manual
                          </button>
                        )}
                      </div>
                      {editingId === f.id ? (
                        <div className="mt-2">
                          <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)}
                            data-testid={`edit-textarea-${f.id}`}
                            className="min-h-[120px] text-sm leading-relaxed text-[#0f172a]" />
                          <div className="mt-2 flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={cancelEdit} disabled={savingEdit}
                              className="rounded-full">
                              Anuleaza
                            </Button>
                            <Button size="sm" onClick={() => saveEdit(f.id)} disabled={savingEdit}
                              data-testid={`save-edit-${f.id}`}
                              className="rounded-full bg-[#059669] text-white hover:bg-[#047857]">
                              {savingEdit ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                              Salveaza in EPUB
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm leading-relaxed text-[#0f172a]">{f.translation}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl bg-[#0f172a] p-4 text-white">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-mono2">
                      <Sparkles className="h-3 w-3 text-[#d97706]" /> De ce a fost semnalat
                    </div>
                    <p className="mt-2 text-sm text-slate-100">{f.reason}</p>
                    {f.proposed_correction && (
                      <div className="mt-3 rounded-lg bg-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-300 font-mono2">Corectie propusa</div>
                        <p className="mt-1.5 text-sm text-white">{f.proposed_correction}</p>
                      </div>
                    )}
                    {!f.proposed_correction && f.lt_suggestions && f.lt_suggestions.length > 0 && (
                      <div className="mt-3 rounded-lg bg-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-300 font-mono2">Sugestie LanguageTool (se aplica la "Aplica corectia")</div>
                        <p className="mt-1.5 text-sm text-white">{f.lt_suggestions[0]}{f.lt_suggestions.length > 1 && <span className="text-slate-400"> &nbsp;(alternative: {f.lt_suggestions.slice(1).join(", ")})</span>}</p>
                      </div>
                    )}
                    {!f.proposed_correction && (!f.lt_suggestions || f.lt_suggestions.length === 0) && f.category === "languagetool" && (
                      <p className="mt-3 text-xs text-slate-400">Semnalare doar informativa -- LanguageTool nu a propus un inlocuitor. Editeaza pasajul manual daca e cazul, apoi respinge semnalarea.</p>
                    )}
                  </div>

                  {f.status === "open" && editingId !== f.id && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button data-testid={`dismiss-${f.id}`} variant="outline" onClick={() => act(f.id, "dismiss")}
                        className="rounded-full border-[#e6e4dd] hover:bg-slate-900 hover:text-white">
                        <X className="mr-1.5 h-4 w-4" /> Pastreaza traducerea
                      </Button>
                      <Button data-testid={`accept-${f.id}`} onClick={() => act(f.id, "accept")}
                        disabled={!f.proposed_correction && !(f.lt_suggestions && f.lt_suggestions.length > 0)}
                        className="rounded-full bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-40">
                        <Check className="mr-1.5 h-4 w-4" /> Aplica corectia
                      </Button>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            <div className="flex justify-center pt-4">
              <Button onClick={() => navigate(`/project/${id}`)} data-testid="review-done-btn"
                className="rounded-full bg-[#0f172a] px-6 text-white hover:bg-[#1e293b]">
                Done reviewing <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
